import { RetryMessage } from '../../utils/retryManager';

export interface Workflow {
  id: string;
  key?: string;
  name: string;
  type: WorkflowTypes;
  subtype: WorkflowSubtype;
  schedule: string; // cron pattern
  lifecycle: WorkflowLifecycle;
  description?: string;
  continuous: boolean;
  backfill: boolean;
  args?: MigrateContactsArgs;
  created: number;
  modified?: number;
  last_execution?: number;
  next_execution?: number;
  last_execution_result?: ExecutionResult;
  last_successful_execution?: number; // set when a daily run completes without contact migration failures
  status: State;
  scheduler_id: string;
  leases?: Lease[];
  error?: string;
  retries?: number;
  /** @deprecated No longer used for new business rules; retained for legacy workflows. */
  endDate?: number;
  isArchived?: boolean;
  /** Consecutive daily runs that found zero valid contacts to migrate. */
  zero_contact_streak?: number;
  /** Unix timestamp set when workflow is paused (used by archival cron). */
  paused_at?: number;
}

export interface Lease {
  [keyof: string]: number;
}

export enum WorkflowTypes {
  MIGRATE_CONTACTS = 'migrate_contacts',
}

export enum WorkflowSubtype {
  EXILE = 'exile',
  DATA_TRANSFER = 'data_transfer',
  DATA_MIGRATION = 'data_migration',
}

export type WorkflowLifecycle = {
  type: WorkflowLifecycleType;
  stage: WorkflowLifecycleStage;
  counter: number;
};

export enum WorkflowLifecycleType {
  ONE_STAGE_7D = 'one_stage_7d',
  THREE_STAGE_6M = 'three_stage_6m',
}

export enum WorkflowLifecycleStage {
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
}

export const WorkflowLifecycleDefault: Record<
  WorkflowLifecycleType,
  WorkflowLifecycle
> = {
  [WorkflowLifecycleType.ONE_STAGE_7D]: {
    type: WorkflowLifecycleType.ONE_STAGE_7D,
    stage: WorkflowLifecycleStage.DAYS,
    counter: 0,
  },
  [WorkflowLifecycleType.THREE_STAGE_6M]: {
    type: WorkflowLifecycleType.THREE_STAGE_6M,
    stage: WorkflowLifecycleStage.DAYS,
    counter: 0,
  },
};

export enum State {
  DELETED = 'deleted',
  DISABLED = 'disabled',
  ENABLED = 'enabled',
  PAUSED = 'paused',
  STATE_UNSPECIFIED = 'state_unspecified',
  UPDATE_FAILED = 'update_failed',
}

export enum ExecutionResult {
  SUCCESS = 'success',
  ERROR = 'error',
  /** Visible for the full daily run; retries do not change status mid-run. */
  RUNNING = 'running',
  /** @deprecated Written by legacy code paths only; UI should treat as running. */
  RETRYING = 'retrying',
}

export enum WorkflowState {
  RESUME = 'resume',
  PAUSE = 'pause',
  RUN_NOW = 'run_now',
}

export interface MigrateContactsArgs extends RetryMessage {
  workflow_id: string;
  source_account: number;
  source_addressbook: number;
  destination_account: number;
  destination_addressbook: number;
  form_id: string;
  batch_size?: number;
  batch_offset?: number; // record to start from
  batch_number?: number; // used for lease identifiers
  /** Carried across batch republishes; false only after non-retryable contact failures. */
  successful_execution?: boolean;
  is_privacy?: boolean;
  /** Frontend-only flag for store destination workflows (legacy; not used in processing). */
  is_store?: boolean;
  /** True if any batch in this daily run fetched contacts to process. */
  contacts_found_in_run?: boolean;
}

export class WorkflowError extends Error {}
export class WorkflowInvalidSubtype extends WorkflowError {}
// Legacy lifecycle/endDate error classes removed — archival handled by archiveEligibility cron
